import { getMe } from './me'
import { el } from './ui'
import './ai-consent.css'

// Asking before anything goes to an outside AI service (Apple 5.1.2(i); Liam, 4 Nov 2026: "Yes" to design A, with Astra's
// conditions): the first time someone asks a Help question, or records a voice note, a sheet says what leaves the app,
// who gets it and why, with "Allow ..." and a plain no. Nothing is sent before a yes; a no still works (Help shows its
// answers to browse; a voice note is kept without being written out). Each choice is separate, kept on this phone for this
// sign-in, and can be changed on Your account (src/screens/account.ts).

export type AiUse = 'help' | 'voice'
type Choice = 'yes' | 'no'

const key = (use: AiUse): string => `roundly:ai-consent:${use}:${(getMe()?.email ?? 'signed-out').toLowerCase()}`

export function aiChoice(use: AiUse): Choice | null {
  try { const v = localStorage.getItem(key(use)); return v === 'yes' || v === 'no' ? v : null } catch { return null }
}

export function setAiChoice(use: AiUse, choice: Choice): void {
  try { localStorage.setItem(key(use), choice) } catch { /* private browsing: asked again next time */ }
}

const WORDS: Record<AiUse, { title: string; who: string; why: string; yes: string; no: string; kept: string }> = {
  help: {
    title: 'Answers from an AI service',
    who: 'Your question goes to OpenRouter, an AI service, which picks the Help answer that fits. Roundly also keeps the question so we can write better answers.',
    why: "Please don't type a customer's name, address or codes.",
    yes: 'Allow AI answers',
    no: 'Not now',
    kept: 'Not now: you can still read every answer below. Change it on Your account.',
  },
  voice: {
    title: 'Write out your voice notes?',
    who: 'The recording goes to Groq, an AI service, which writes out what you said so the note can be read and searched.',
    why: "Say no and your voice notes are kept as recordings only. Either way the recording stays with the job.",
    yes: 'Allow writing out',
    no: 'Keep recordings only',
    kept: 'Recordings only. Change it on Your account.',
  },
}

// Asked once: resolves true for yes, false for no. An earlier answer answers at once, without a sheet.
export function askAi(use: AiUse): Promise<boolean> {
  const before = aiChoice(use)
  if (before) return Promise.resolve(before === 'yes')
  const w = WORDS[use]
  return new Promise((resolve) => {
    const dialog = el('dialog', { class: 'ai-consent', 'aria-label': w.title }) as HTMLDialogElement
    const yes = el('button', { type: 'button', class: 'btn-primary ai-consent-yes' }, w.yes)
    const no = el('button', { type: 'button', class: 'btn-secondary ai-consent-no' }, w.no)
    let answered = false
    const finish = (choice: Choice | null): void => {
      if (answered) return
      answered = true
      if (choice) setAiChoice(use, choice)
      if (dialog.open) dialog.close()
      dialog.remove()
      resolve(choice === 'yes')
    }
    yes.addEventListener('click', () => finish('yes'))
    no.addEventListener('click', () => finish('no'))
    // Closed without an answer: nothing is sent this time, and it asks again next time.
    dialog.addEventListener('cancel', (e) => { e.preventDefault(); finish(null) })
    dialog.append(el('div', { class: 'ai-consent-body' },
      el('h2', {}, w.title), el('p', {}, w.who), el('p', { class: 'muted' }, w.why),
      el('div', { class: 'ai-consent-buttons' }, yes, no)))
    document.body.append(dialog)
    dialog.showModal()
    yes.focus()
  })
}

export const aiKeptWords = (use: AiUse): string => WORDS[use].kept

// Your account: one row per use, saying what it does now and a button to change it.
export function aiRows(): HTMLElement {
  const box = el('section', { class: 'ai-rows', 'aria-label': 'AI services' }, el('h3', {}, 'AI services'))
  for (const use of ['help', 'voice'] as AiUse[]) {
    const row = el('div', { class: 'ai-row' })
    const draw = (): void => {
      const c = aiChoice(use)
      const label = use === 'help' ? 'Help answers (OpenRouter)' : 'Voice notes written out (Groq)'
      const state = c === 'yes' ? 'Allowed' : c === 'no' ? 'Off' : 'Asks the first time'
      const flip = el('button', { type: 'button', class: 'btn-secondary ai-row-button' }, c === 'yes' ? 'Turn off' : 'Allow')
      flip.addEventListener('click', () => { setAiChoice(use, c === 'yes' ? 'no' : 'yes'); draw() })
      row.replaceChildren(el('div', {}, el('strong', {}, label), el('span', { class: 'muted ai-row-state' }, state)), flip)
    }
    draw()
    box.append(row)
  }
  return box
}
